/**
 * What a failed transcription MEANS, in words rather than in a code.
 *
 * `CallLog.transcriptError` holds whatever the transcription worker raised, and
 * the panel printed it verbatim: "Transcription failed: audio_too_large". That
 * is a note the worker wrote to itself. It tells the operator neither what went
 * wrong nor whether pressing retry can possibly help, which are the only two
 * things they can act on.
 *
 * ⚠ AN UNKNOWN CODE IS RETURNED UNTRANSLATED ON PURPOSE. The worker also stores
 * free-form provider text - a truncated API message, `blocked: SAFETY` - and
 * there is no useful sentence to write for a string nobody has seen yet.
 * Guessing one would replace an ugly true message with a pretty false one. So
 * the map is a whitelist, and everything outside it falls through to the raw
 * text exactly as before.
 *
 * React-free and in its own file so the mapping can be tested without a panel,
 * and so the list stays somewhere a person can compare against the worker's
 * throw sites (`lib/transcription.ts`, `lib/silence.ts`, `lib/gemini.ts`).
 */

/**
 * The codes the CRM's own transcription path raises, each mapped to the tail of
 * an i18n key under `calls.transcript.why.`.
 *
 * Anything the model provider invents is deliberately absent - see the header.
 */
const KNOWN: Record<string, string> = {
  // The recording could not be made small enough to send. After the re-encode
  // in `lib/audio-downmix.ts` this means a genuinely unusable file, not a long
  // call, so the message must not tell anybody to try again.
  audio_too_large: "tooLarge",
  no_recording: "noRecording",
  recording_missing: "recordingMissing",
  recording_too_quiet: "tooQuiet",
  elevenlabs_empty_transcript: "noSpeech",
  rate_limited: "rateLimited",
  network: "network",
  invalid_json: "modelAnswer",
  missing_candidates: "modelAnswer",
  internal: "internal",
};

/**
 * The i18n key for a stored transcript error, or null when the code is not one
 * of ours and the raw text is the honest thing to show.
 */
export function transcriptErrorKey(code: string | null | undefined): string | null {
  if (!code) return null;
  const tail = KNOWN[code.trim()];
  return tail ? `calls.transcript.why.${tail}` : null;
}

/**
 * Whether pressing retry on this failure can plausibly change the answer.
 *
 * Used only to decide whether the panel says so, never to hide the button: the
 * operator may know something we do not (the recording was re-uploaded, the key
 * was fixed), and a button that refuses to exist cannot be argued with.
 */
export function transcriptErrorIsPermanent(code: string | null | undefined): boolean {
  if (!code) return false;
  return ["audio_too_large", "no_recording", "recording_missing", "recording_too_quiet"].includes(
    code.trim(),
  );
}
